import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faPlay, faExclamationTriangle, faCheckCircle,
  faTimesCircle, faSearch, faShieldAlt, faHeartbeat,
  faMousePointer, faKeyboard, faListAlt, faSync, faCircle,
  faDownload, faFileCode, faFileCsv, faTrash, faSave, faClock
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';

// ── Export helpers ──────────────────────────────────────────────────────────
const downloadBlob = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const escCsv = (v) => {
  const s = String(v ?? '');
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (diff, replayResults, baselineLabel, testLabel) => {
  const lines = [];
  lines.push('# VWG AuthZ Tester — Export');
  lines.push(`# Baseline: ${baselineLabel}`);
  lines.push(`# Test: ${testLabel}`);
  lines.push(`# Admin-Only: ${diff.summary.baselineOnlyCount}, Shared: ${diff.summary.sharedCount}, Test-Only: ${diff.summary.testOnlyCount}, High-Interest: ${diff.summary.highInterest}`);
  lines.push('');

  lines.push('## Admin-Only Controls');
  lines.push(['Control ID', 'Type', 'Events', 'Label/Values', 'Replay Verdict', 'Replay Status', 'Replay Bytes', 'New Controls'].map(escCsv).join(','));
  const replayMap = {};
  if (replayResults) replayResults.results.forEach(r => { replayMap[r.controlId] = r; });
  for (const c of diff.baselineOnly) {
    const r = replayMap[c.controlId];
    lines.push([
      `SR=${c.controlId}`,
      c.controlType || '',
      c.events.map(e => `${e.tp} x${e.count}`).join('; '),
      c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 60) : ''),
      r?.verdict || '',
      r?.responseStatus || '',
      r?.responseLength ?? '',
      r?.newControlCount ?? ''
    ].map(escCsv).join(','));
  }
  return lines.join('\n');
};

const buildHtmlReport = (diff, replayResults, baselineLabel, testLabel, targetName) => {
  const now = new Date().toLocaleString('de-CH');
  const vulnerable = replayResults ? replayResults.results.filter(r => r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE') : [];
  const protected_ = replayResults ? replayResults.results.filter(r => r.verdict === 'IGNORED' || r.verdict === 'MINIMAL') : [];
  const replayMap = {};
  if (replayResults) replayResults.results.forEach(r => { replayMap[r.controlId] = r; });

  const verdictStyle = (v) => {
    const m = { DATA_RETURNED: '#dc2626', LARGE_RESPONSE: '#ea580c', ERROR: '#ca8a04', IGNORED: '#16a34a', MINIMAL: '#15803d', NO_RESPONSE: '#6b7280', NO_EVENTS: '#6b7280' };
    return `background:${m[v] || '#6b7280'};color:#fff;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700;`;
  };

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>VWG AuthZ Report — ${targetName || 'Target'}</title>
<style>
  :root{--bg:#fff;--fg:#111;--muted:#666;--border:#e5e7eb;--card:#f9fafb;--red:#fef2f2;--green:#f0fdf4;--teal:#0d9488}
  @media(prefers-color-scheme:dark){:root{--bg:#111827;--fg:#f3f4f6;--muted:#9ca3af;--border:#374151;--card:#1f2937;--red:#450a0a;--green:#052e16;--teal:#14b8a6}}
  *{margin:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:var(--bg);color:var(--fg);padding:32px;max-width:1100px;margin:0 auto;font-size:14px;line-height:1.5}
  h1{font-size:22px;margin-bottom:4px}h2{font-size:16px;margin:24px 0 8px;border-bottom:1px solid var(--border);padding-bottom:4px}
  .meta{color:var(--muted);font-size:12px;margin-bottom:20px}.badge{display:inline-block;padding:3px 10px;border-radius:6px;font-size:12px;font-weight:600;margin-right:8px}
  .b-red{background:#ef4444;color:#fff}.b-green{background:#22c55e;color:#fff}.b-blue{background:#3b82f6;color:#fff}.b-orange{background:#f97316;color:#fff}
  table{width:100%;border-collapse:collapse;font-size:12px;margin:8px 0}th,td{padding:6px 10px;text-align:left;border-bottom:1px solid var(--border)}
  th{background:var(--card);font-weight:600;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.5px}
  tr.vuln{background:var(--red)}tr.safe{background:var(--green)}.mono{font-family:'SF Mono',Consolas,monospace;font-size:12px}
  .summary-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin:16px 0}
  .summary-card{background:var(--card);border:1px solid var(--border);border-radius:8px;padding:16px;text-align:center}
  .summary-card .num{font-size:28px;font-weight:700}.summary-card .lbl{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.5px}
  .verdict{padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700;color:#fff;white-space:nowrap}
  .event-tag{display:inline-block;padding:1px 6px;border-radius:3px;font-size:10px;margin:1px;background:var(--card);border:1px solid var(--border)}
  .event-tag.hot{background:#fdba74;color:#7c2d12;border-color:#f97316}
  @media print{body{padding:16px}h1{font-size:18px}table{font-size:10px}th,td{padding:4px 6px}}
</style>
</head>
<body>
<h1>VWG Authorization Test Report</h1>
<div class="meta">
  <strong>Target:</strong> ${targetName || '—'}<br>
  <strong>Baseline (Admin):</strong> ${baselineLabel}<br>
  <strong>Test (Low-Priv):</strong> ${testLabel}<br>
  <strong>Datum:</strong> ${now}
</div>

<h2>Zusammenfassung</h2>
<div class="summary-grid">
  <div class="summary-card"><div class="num" style="color:#ef4444">${diff.summary.baselineOnlyCount}</div><div class="lbl">Nur Admin</div></div>
  <div class="summary-card"><div class="num" style="color:#22c55e">${diff.summary.sharedCount}</div><div class="lbl">Gemeinsam</div></div>
  <div class="summary-card"><div class="num" style="color:#3b82f6">${diff.summary.testOnlyCount}</div><div class="lbl">Nur Test</div></div>
  <div class="summary-card"><div class="num" style="color:#f97316">${diff.summary.highInterest}</div><div class="lbl">High-Interest</div></div>
</div>

${replayResults ? `
<div class="summary-grid">
  <div class="summary-card" style="border-color:#ef4444"><div class="num" style="color:#ef4444">${vulnerable.length}</div><div class="lbl">Mögl. verwundbar</div></div>
  <div class="summary-card" style="border-color:#22c55e"><div class="num" style="color:#22c55e">${protected_.length}</div><div class="lbl">Geschützt</div></div>
</div>
` : '<p style="color:var(--muted);font-style:italic">Kein Replay durchgeführt — nur Analyse-Ergebnisse.</p>'}

<h2>Admin-Only Controls (${diff.baselineOnly.length})</h2>
<table>
<thead><tr><th>Control</th><th>Typ</th><th>Events</th><th>Label / Werte</th>${replayResults ? '<th>Verdict</th><th>Status</th><th>Bytes</th>' : ''}</tr></thead>
<tbody>
${diff.baselineOnly.map(c => {
  const r = replayMap[c.controlId];
  const isVuln = r && (r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
  const isSafe = r && (r.verdict === 'IGNORED' || r.verdict === 'MINIMAL');
  return `<tr class="${isVuln ? 'vuln' : isSafe ? 'safe' : ''}">
    <td class="mono">SR=${c.controlId}</td>
    <td>${c.controlType || '—'}</td>
    <td>${c.events.map(e => `<span class="event-tag${['Click','ValueChange','SelectionChanged'].includes(e.tp) ? ' hot' : ''}">${e.tp} x${e.count}</span>`).join(' ')}</td>
    <td>${c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 60) : '—')}</td>
    ${r ? `<td><span class="verdict" style="${verdictStyle(r.verdict)}">${r.verdict}</span></td><td>${r.responseStatus || '—'}</td><td class="mono" style="text-align:right">${r.responseLength != null ? r.responseLength.toLocaleString() : '—'}</td>` : (replayResults ? '<td>—</td><td>—</td><td>—</td>' : '')}
  </tr>`;
}).join('\n')}
</tbody>
</table>

${diff.shared && diff.shared.length > 0 ? `
<h2>Gemeinsame Controls (${diff.shared.length})</h2>
<table>
<thead><tr><th>Control</th><th>Typ</th><th>Events</th></tr></thead>
<tbody>
${diff.shared.map(c => `<tr><td class="mono">SR=${c.controlId}</td><td>${c.controlType || '—'}</td><td>${c.events.map(e => `<span class="event-tag">${e.tp} x${e.count}</span>`).join(' ')}</td></tr>`).join('\n')}
</tbody>
</table>
` : ''}

${diff.testOnly && diff.testOnly.length > 0 ? `
<h2>Nur Test-Session (${diff.testOnly.length})</h2>
<table>
<thead><tr><th>Control</th><th>Typ</th><th>Events</th></tr></thead>
<tbody>
${diff.testOnly.map(c => `<tr><td class="mono">SR=${c.controlId}</td><td>${c.controlType || '—'}</td><td>${c.events.map(e => `<span class="event-tag">${e.tp} x${e.count}</span>`).join(' ')}</td></tr>`).join('\n')}
</tbody>
</table>
` : ''}

<div style="margin-top:32px;padding-top:12px;border-top:1px solid var(--border);color:var(--muted);font-size:11px">
  PurpleHQ VWG AuthZ Tester — Report generiert am ${now}
</div>
</body></html>`;
};

const EVENT_ICONS = {
  Click: faMousePointer,
  ValueChange: faKeyboard,
  SelectionChanged: faListAlt,
};

const VERDICT_COLORS = {
  DATA_RETURNED: { bg: 'bg-red-600', text: 'text-white', label: 'DATA RETURNED' },
  LARGE_RESPONSE: { bg: 'bg-orange-500', text: 'text-white', label: 'LARGE RESPONSE' },
  ERROR: { bg: 'bg-yellow-600', text: 'text-white', label: 'ERROR' },
  IGNORED: { bg: 'bg-green-600', text: 'text-white', label: 'IGNORED (protected)' },
  MINIMAL: { bg: 'bg-green-700', text: 'text-white', label: 'MINIMAL' },
  NO_RESPONSE: { bg: 'bg-gray-500', text: 'text-white', label: 'NO RESPONSE' },
  NO_EVENTS: { bg: 'bg-gray-500', text: 'text-white', label: 'NO EVENTS' },
};

const SessionStatusDot = ({ status, isDarkMode }) => {
  if (status === 'checking') return <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-[10px]" />;
  if (status === 'alive') return <FontAwesomeIcon icon={faCircle} className="text-green-500 text-[8px]" />;
  if (status === 'expired') return <FontAwesomeIcon icon={faCircle} className="text-red-500 text-[8px]" />;
  return <FontAwesomeIcon icon={faCircle} className="text-gray-500 text-[8px]" />;
};

const VwgAuthzTester = ({ webTarget, isDarkMode }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baselineVwg, setBaselineVwg] = useState('');
  const [testVwg, setTestVwg] = useState('');
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [replayResults, setReplayResults] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [selectedControls, setSelectedControls] = useState(new Set());
  const [error, setError] = useState('');

  // Session status tracking
  const [sessionStatus, setSessionStatus] = useState({}); // vwg → 'alive'|'expired'|'checking'
  const [keepAliveActive, setKeepAliveActive] = useState(new Set());
  const keepAliveIntervals = useRef({});

  // Saved analyses
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [activeAnalysisId, setActiveAnalysisId] = useState(null);
  const [saving, setSaving] = useState(false);
  const replayResultsRef = useRef(null);

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const wtId = webTarget?._id;

  const loadSessions = useCallback(async () => {
    if (!wtId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/sessions`);
      setSessions(data);
      // Auto-probe all sessions in background
      for (const s of data) {
        setSessionStatus(prev => ({ ...prev, [s.vwginstance]: 'checking' }));
        axios.get(`/vwg-authz/${wtId}/probe/${s.vwginstance}`)
          .then(r => setSessionStatus(prev => ({ ...prev, [s.vwginstance]: r.data.alive ? 'alive' : 'expired' })))
          .catch(() => setSessionStatus(prev => ({ ...prev, [s.vwginstance]: 'expired' })));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [wtId]);

  const loadAnalyses = useCallback(async () => {
    if (!wtId) return;
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/analyses`);
      setSavedAnalyses(data);
    } catch { /* silent */ }
  }, [wtId]);

  useEffect(() => { loadSessions(); loadAnalyses(); }, [loadSessions, loadAnalyses]);

  // Cleanup keep-alive intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(keepAliveIntervals.current).forEach(clearInterval);
    };
  }, []);

  const probeSession = async (vwg) => {
    setSessionStatus(prev => ({ ...prev, [vwg]: 'checking' }));
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/probe/${vwg}`);
      setSessionStatus(prev => ({ ...prev, [vwg]: data.alive ? 'alive' : 'expired' }));
      return data;
    } catch {
      setSessionStatus(prev => ({ ...prev, [vwg]: 'expired' }));
      return { alive: false };
    }
  };

  const probeAll = async () => {
    const vwgs = [...new Set([baselineVwg, testVwg].filter(Boolean))];
    await Promise.all(vwgs.map(probeSession));
  };

  const toggleKeepAlive = (vwg) => {
    setKeepAliveActive(prev => {
      const next = new Set(prev);
      if (next.has(vwg)) {
        next.delete(vwg);
        if (keepAliveIntervals.current[vwg]) {
          clearInterval(keepAliveIntervals.current[vwg]);
          delete keepAliveIntervals.current[vwg];
        }
      } else {
        next.add(vwg);
        const send = async () => {
          try {
            const { data } = await axios.post(`/vwg-authz/${wtId}/keepalive/${vwg}`);
            setSessionStatus(p => ({ ...p, [vwg]: data.alive ? 'alive' : 'expired' }));
            if (!data.alive) {
              clearInterval(keepAliveIntervals.current[vwg]);
              delete keepAliveIntervals.current[vwg];
              setKeepAliveActive(p => { const n = new Set(p); n.delete(vwg); return n; });
            }
          } catch {
            setSessionStatus(p => ({ ...p, [vwg]: 'expired' }));
          }
        };
        send();
        keepAliveIntervals.current[vwg] = setInterval(send, 60000);
      }
      return next;
    });
  };

  const findNewSession = async () => {
    setError('');
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/newest-session`, {
        params: { exclude: baselineVwg }
      });
      if (data.alive) {
        await loadSessions();
        setTestVwg(data.vwginstance);
        setSessionStatus(prev => ({ ...prev, [data.vwginstance]: 'alive' }));
      } else {
        setError('Keine aktive neue Session gefunden. Logge dich mit dem Low-Priv-User über den Proxy ein und versuche es nochmals.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const runDiff = async () => {
    if (!baselineVwg || !testVwg) return;
    setDiffLoading(true);
    setReplayResults(null);
    setError('');
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/diff`, { baselineVwg, testVwg });
      setDiff(data);
      const autoSelect = new Set(
        data.baselineOnly
          .filter(c => c.events.some(e => ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)))
          .map(c => c.controlId)
      );
      setSelectedControls(autoSelect);
      // Auto-save analysis
      const bl = sessions.find(s => s.vwginstance === baselineVwg);
      const tl = sessions.find(s => s.vwginstance === testVwg);
      try {
        const { data: saved } = await axios.post(`/vwg-authz/${wtId}/analyses`, {
          baselineVwg, testVwg,
          baselineLabel: bl ? sessionLabel(bl) : baselineVwg,
          testLabel: tl ? sessionLabel(tl) : testVwg,
          diff: data,
          selectedControlIds: Array.from(autoSelect),
        });
        setActiveAnalysisId(saved._id);
        loadAnalyses();
      } catch { /* save is best-effort */ }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const runReplay = async () => {
    if (!selectedControls.size) return;
    const testSession = sessions.find(s => s.vwginstance === testVwg);
    if (!testSession) return;
    setReplayLoading(true);
    setReplayResults(null);
    setError('');
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/replay`, {
        baselineVwg,
        targetCookie: testSession.cookie,
        targetVwginstance: testVwg,
        controlIds: Array.from(selectedControls),
      });
      setReplayResults(data);
      setTimeout(() => replayResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      // Save replay results to active analysis
      if (activeAnalysisId) {
        try {
          await axios.put(`/vwg-authz/${wtId}/analyses/${activeAnalysisId}`, {
            replayResults: data,
            selectedControlIds: Array.from(selectedControls),
          });
          loadAnalyses();
        } catch { /* best-effort */ }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setReplayLoading(false);
    }
  };

  const loadSavedAnalysis = (analysis) => {
    setDiff(analysis.diff);
    setReplayResults(analysis.replayResults || null);
    setSelectedControls(new Set(analysis.selectedControlIds || []));
    setBaselineVwg(analysis.baselineVwg);
    setTestVwg(analysis.testVwg);
    setActiveAnalysisId(analysis._id);
    setError('');
  };

  const deleteSavedAnalysis = async (id) => {
    try {
      await axios.delete(`/vwg-authz/${wtId}/analyses/${id}`);
      setSavedAnalyses(prev => prev.filter(a => a._id !== id));
      if (activeAnalysisId === id) {
        setActiveAnalysisId(null);
        setDiff(null);
        setReplayResults(null);
        setSelectedControls(new Set());
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleControl = (cid) => {
    setSelectedControls(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const selectAll = () => {
    if (!diff) return;
    setSelectedControls(new Set(diff.baselineOnly.map(c => c.controlId)));
  };

  const selectNone = () => setSelectedControls(new Set());

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-teal-500" />
      </div>
    );
  }

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  };

  const sessionLabel = (s, includeStatus) => {
    const name = s.profileName ? s.profileName.replace(/^Auto:\s*/i, '') : s.vwginstance.substring(0, 8) + '…';
    const status = sessionStatus[s.vwginstance];
    const prefix = includeStatus ? (status === 'alive' ? '● ' : status === 'expired' ? '✗ ' : status === 'checking' ? '… ' : '? ') : '';
    return `${prefix}${name} | ${s.controlCount ?? '?'} Ctrls | ${s.exchangeCount} Req | ${formatTime(s.firstSeen)}–${formatTime(s.lastSeen)}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`p-4 rounded-lg border ${cardBg}`}>
        <h3 className={`text-lg font-bold mb-1 ${textPrimary}`}>
          <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-teal-500" />
          VWG AuthZ Tester
        </h3>
        <p className={`text-sm ${textSecondary}`}>
          Vergleicht VWG-Control-Events zwischen Rollen und replayed Admin-Events durch Low-Priv-Sessions.
        </p>
      </div>

      {error && (
        <div className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-red-900/30 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* Session Selector */}
      {sessions.length === 0 && !loading ? (
        <div className={`p-6 text-center rounded-lg border ${cardBg}`}>
          <p className={textSecondary}>Keine VWG-Sessions gefunden. Starte den Proxy und browse die App.</p>
        </div>
      ) : (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Sessions auswählen</h4>
            <div className="flex gap-2">
              <button
                onClick={probeAll}
                disabled={!baselineVwg && !testVwg}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
              >
                <FontAwesomeIcon icon={faHeartbeat} /> Status prüfen
              </button>
              <button
                onClick={loadSessions}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <FontAwesomeIcon icon={faSync} /> Aktualisieren
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Baseline (Admin) */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${textSecondary}`}>
                Baseline (Admin / High-Priv)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={baselineVwg}
                  onChange={e => { setBaselineVwg(e.target.value); setDiff(null); setReplayResults(null); }}
                  className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="">— Session wählen —</option>
                  {sessions.map(s => (
                    <option key={s.vwginstance} value={s.vwginstance}>
                      {sessionLabel(s, true)}
                    </option>
                  ))}
                </select>
                {baselineVwg && <SessionStatusDot status={sessionStatus[baselineVwg]} isDarkMode={isDarkMode} />}
              </div>
              {baselineVwg && (
                <div className="mt-1 flex items-center gap-2">
                  <button
                    onClick={() => toggleKeepAlive(baselineVwg)}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      keepAliveActive.has(baselineVwg)
                        ? 'bg-green-600 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <FontAwesomeIcon icon={faHeartbeat} className="mr-1" />
                    {keepAliveActive.has(baselineVwg) ? 'Keep-Alive aktiv' : 'Keep-Alive'}
                  </button>
                  {sessionStatus[baselineVwg] === 'expired' && (
                    <span className="text-[10px] text-red-400">Session abgelaufen — Analyse funktioniert trotzdem (historische Daten)</span>
                  )}
                </div>
              )}
            </div>

            {/* Test (Low-Priv) */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${textSecondary}`}>
                Test (Low-Priv / Andere Rolle)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={testVwg}
                  onChange={e => { setTestVwg(e.target.value); setReplayResults(null); }}
                  className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="">— Session wählen —</option>
                  {sessions.map(s => (
                    <option key={s.vwginstance} value={s.vwginstance}>
                      {sessionLabel(s, true)}
                    </option>
                  ))}
                </select>
                {testVwg && <SessionStatusDot status={sessionStatus[testVwg]} isDarkMode={isDarkMode} />}
              </div>
              <div className="mt-1 flex items-center gap-2">
                {testVwg && (
                  <button
                    onClick={() => toggleKeepAlive(testVwg)}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      keepAliveActive.has(testVwg)
                        ? 'bg-green-600 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <FontAwesomeIcon icon={faHeartbeat} className="mr-1" />
                    {keepAliveActive.has(testVwg) ? 'Keep-Alive aktiv' : 'Keep-Alive'}
                  </button>
                )}
                {sessionStatus[testVwg] === 'expired' && (
                  <button
                    onClick={findNewSession}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-orange-600 text-white hover:bg-orange-700"
                  >
                    <FontAwesomeIcon icon={faSync} className="mr-1" />
                    Neue Session nach Re-Login suchen
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={runDiff}
              disabled={!baselineVwg || !testVwg || baselineVwg === testVwg || diffLoading}
              className="bg-teal-600 text-white px-4 py-2 rounded text-sm hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
            >
              {diffLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSearch} />}
              Analysieren
            </button>
            {baselineVwg === testVwg && baselineVwg && (
              <span className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                Baseline und Test müssen verschieden sein
              </span>
            )}
          </div>
        </div>
      )}

      {/* Saved Analyses Tabs */}
      {savedAnalyses.length > 0 && (
        <div className={`rounded-lg border ${cardBg}`}>
          <div className="flex items-center overflow-x-auto">
            {savedAnalyses.map(a => {
              const isActive = activeAnalysisId === a._id;
              const ts = new Date(a.createdAt).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
              const hasReplay = !!a.replayResults;
              return (
                <div
                  key={a._id}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs cursor-pointer border-b-2 whitespace-nowrap ${
                    isActive
                      ? 'border-teal-500 ' + (isDarkMode ? 'text-teal-400 bg-gray-700/50' : 'text-teal-700 bg-teal-50')
                      : 'border-transparent ' + (isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/30' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                  }`}
                  onClick={() => loadSavedAnalysis(a)}
                >
                  <FontAwesomeIcon icon={faClock} className="text-[9px]" />
                  <span>{ts}</span>
                  {hasReplay && <span className={`text-[9px] px-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>Replay</span>}
                  <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    {a.diff?.summary?.baselineOnlyCount || '?'} Admin-Only
                  </span>
                  <button
                    onClick={e => { e.stopPropagation(); deleteSavedAnalysis(a._id); }}
                    className={`ml-1 text-[9px] px-1 rounded hover:bg-red-900/30 ${isDarkMode ? 'text-gray-600 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                    title="Analyse löschen"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diff Results */}
      {diff && (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <div className="flex items-center gap-6 mb-4 flex-wrap">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Ergebnis</h4>
            <div className="flex gap-4 text-xs flex-wrap">
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'}`}>
                Nur Admin: {diff.summary.baselineOnlyCount}
              </span>
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'}`}>
                Gemeinsam: {diff.summary.sharedCount}
              </span>
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                Nur Test: {diff.summary.testOnlyCount}
              </span>
              <span className={`px-2 py-1 rounded font-semibold ${isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-50 text-orange-700'}`}>
                High-Interest: {diff.summary.highInterest}
              </span>
            </div>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => {
                  const bl = sessions.find(s => s.vwginstance === baselineVwg);
                  const tl = sessions.find(s => s.vwginstance === testVwg);
                  const csv = buildCsv(diff, replayResults, bl ? sessionLabel(bl) : baselineVwg, tl ? sessionLabel(tl) : testVwg);
                  downloadBlob(csv, `vwg-authz-${webTarget?.name || 'export'}.csv`, 'text/csv;charset=utf-8');
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title="Export als CSV"
              >
                <FontAwesomeIcon icon={faFileCsv} /> CSV
              </button>
              <button
                onClick={() => {
                  const bl = sessions.find(s => s.vwginstance === baselineVwg);
                  const tl = sessions.find(s => s.vwginstance === testVwg);
                  const html = buildHtmlReport(diff, replayResults, bl ? sessionLabel(bl) : baselineVwg, tl ? sessionLabel(tl) : testVwg, webTarget?.name || webTarget?.baseUrl);
                  downloadBlob(html, `vwg-authz-report-${webTarget?.name || 'export'}.html`, 'text/html;charset=utf-8');
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title="Export als HTML Report"
              >
                <FontAwesomeIcon icon={faFileCode} /> HTML
              </button>
            </div>
          </div>

          {diff.baselineOnly.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <h5 className={`text-xs font-semibold ${textSecondary}`}>
                  Admin-Only Controls (zum Replay auswählen)
                </h5>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-teal-500 hover:underline">Alle</button>
                  <button onClick={selectNone} className="text-xs text-teal-500 hover:underline">Keine</button>
                </div>
              </div>
              <div className={`max-h-80 overflow-y-auto rounded border ${borderColor}`}>
                <table className="w-full text-xs">
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8"></th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Control ID</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Typ</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Events</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Label/Werte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.baselineOnly.map(c => {
                      const hasInteresting = c.events.some(e =>
                        ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)
                      );
                      return (
                        <tr
                          key={c.controlId}
                          onClick={() => toggleControl(c.controlId)}
                          className={`cursor-pointer border-t ${borderColor} ${
                            selectedControls.has(c.controlId)
                              ? isDarkMode ? 'bg-teal-900/30' : 'bg-teal-50'
                              : isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={selectedControls.has(c.controlId)}
                              onChange={() => toggleControl(c.controlId)}
                              className="rounded"
                            />
                          </td>
                          <td className={`px-2 py-1.5 font-mono ${hasInteresting ? 'font-bold text-orange-400' : textPrimary}`}>
                            SR={c.controlId}
                          </td>
                          <td className={`px-2 py-1.5 ${textSecondary}`}>
                            {c.controlType || '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex gap-1 flex-wrap">
                              {c.events.map(e => (
                                <span
                                  key={e.tp}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)
                                      ? isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700'
                                      : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                  }`}
                                >
                                  {EVENT_ICONS[e.tp] && <FontAwesomeIcon icon={EVENT_ICONS[e.tp]} className="text-[9px]" />}
                                  {e.tp} x{e.count}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className={`px-2 py-1.5 ${textSecondary} max-w-48 truncate`}>
                            {c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 40) : '—')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Replay Button */}
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button
                  onClick={runReplay}
                  disabled={!selectedControls.size || replayLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {replayLoading
                    ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    : <FontAwesomeIcon icon={faPlay} />
                  }
                  Replay {selectedControls.size} Controls durch Low-Priv-Session
                </button>
                {sessionStatus[testVwg] === 'expired' && (
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Test-Session abgelaufen — Replay wird fehlschlagen. Neu einloggen und "Neue Session suchen".
                  </span>
                )}
                {sessionStatus[testVwg] !== 'expired' && !replayResults && (
                  <span className={`text-xs ${textSecondary}`}>
                    Sendet Admin-Events mit den Credentials der Test-Session
                  </span>
                )}
              </div>

              {/* Replay Results (inline, right after button) */}
              {replayResults && (replayResults.results || []).length > 0 && (
                <div ref={replayResultsRef} className="mt-4">
                  <div className="flex items-center gap-3 mb-2">
                    <h5 className={`text-xs font-bold ${textPrimary}`}>Replay-Ergebnisse ({replayResults.results.length} Tests)</h5>
                    {(() => {
                      const vulnerable = replayResults.results.filter(r => r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
                      const protected_ = replayResults.results.filter(r => r.verdict === 'IGNORED' || r.verdict === 'MINIMAL');
                      return (
                        <>
                          {vulnerable.length > 0 && (
                            <span className={`px-2 py-1 rounded text-xs font-bold ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                              {vulnerable.length} mögl. verwundbar
                            </span>
                          )}
                          {protected_.length > 0 && (
                            <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                              {protected_.length} geschützt
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className={`rounded border overflow-x-auto ${borderColor}`}>
                    <table className="w-full text-xs">
                      <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                        <tr>
                          <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Control</th>
                          <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Event</th>
                          <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Status</th>
                          <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Resp Bytes</th>
                          <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Neue Controls</th>
                          <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Verdict</th>
                        </tr>
                      </thead>
                      <tbody>
                        {replayResults.results.map((r, i) => {
                          const vd = VERDICT_COLORS[r.verdict] || VERDICT_COLORS.NO_RESPONSE;
                          const isVulnerable = r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE';
                          return (
                            <tr
                              key={i}
                              className={`border-t ${borderColor} ${
                                isVulnerable
                                  ? isDarkMode ? 'bg-red-900/20' : 'bg-red-50'
                                  : ''
                              }`}
                            >
                              <td className={`px-2 py-1.5 font-mono ${textPrimary}`}>SR={r.controlId}</td>
                              <td className={`px-2 py-1.5 ${textSecondary}`}>
                                {EVENT_ICONS[r.eventType] && <FontAwesomeIcon icon={EVENT_ICONS[r.eventType]} className="mr-1" />}
                                {r.eventType}
                                {r.txValue ? ` "${r.txValue.substring(0, 20)}"` : ''}
                              </td>
                              <td className={`px-2 py-1.5 ${textSecondary}`}>{r.responseStatus || '—'}</td>
                              <td className={`px-2 py-1.5 text-right font-mono ${textSecondary}`}>
                                {r.responseLength != null ? r.responseLength.toLocaleString() : '—'}
                              </td>
                              <td className={`px-2 py-1.5 text-right font-mono ${textSecondary}`}>
                                {r.newControlCount != null ? r.newControlCount : '—'}
                              </td>
                              <td className="px-2 py-1.5">
                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${vd.bg} ${vd.text}`}>
                                  {isVulnerable && <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />}
                                  {r.verdict === 'IGNORED' && <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />}
                                  {vd.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {diff.baselineOnly.length === 0 && (
            <div className={`text-center py-6 ${textSecondary}`}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-2xl mb-2" />
              <p>Keine Admin-Only Controls gefunden — beide Sessions nutzen dieselben Controls.</p>
              <p className="text-xs mt-1">Das kann bedeuten: gleiche Berechtigungen, oder die Admin-Features wurden nicht besucht.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VwgAuthzTester;
